/**
 * JS absolute positioning, not CSS `column-count`.
 *
 * CSS columns fill top-to-bottom within each column, so items read
 * down-then-across instead of across-then-down — the newest item lands in the
 * middle of column one instead of at the top left. Columns also can't animate
 * when the panel opens.
 *
 * Pure and synchronous on purpose: it is the one piece of stage 2 worth
 * testing, and it needs nothing from the DOM. Sizes come from the database, so
 * the whole layout is computable before a single image has decoded.
 */

export const GUTTER = 16;
/** Pinterest's actual column width. */
export const TARGET_COL_W = 236;
/** Title plus optional price. Fixed, so the caption can't push a column out. */
export const CAPTION_H = 44;

/**
 * A screenshot of a whole product page can be 1:8, which produces a tile
 * taller than the viewport and a column wildly out of balance. Clamp the
 * *rendered* ratio and let `object-fit: cover` take the overflow — the full
 * image is still intact in the panel.
 */
export const MAX_RATIO = 2;

/** Everything the layout needs to know about an item. */
export type Sized = {
  id: string;
  w: number;
  h: number;
};

export type Placement = {
  id: string;
  x: number;
  y: number;
  w: number;
  /** The image box. The tile is this plus `CAPTION_H`. */
  h: number;
};

export type Layout = {
  placements: Placement[];
  byId: Map<string, Placement>;
  /** Height of the container the tiles are absolutely positioned inside. */
  height: number;
  colW: number;
  cols: number;
};

export function layout(items: readonly Sized[], containerW: number): Layout {
  const cols = columnCount(containerW);
  const colW = Math.max(1, (containerW - GUTTER * (cols - 1)) / cols);

  const heights = new Array<number>(cols).fill(0);
  const placements: Placement[] = [];
  const byId = new Map<string, Placement>();

  for (const item of items) {
    const c = shortest(heights);
    const y = heights[c] ?? 0;
    const h = colW * ratio(item.w, item.h);

    const placement: Placement = { id: item.id, x: c * (colW + GUTTER), y, w: colW, h };
    placements.push(placement);
    byId.set(item.id, placement);

    heights[c] = y + h + CAPTION_H + GUTTER;
  }

  const tallest = heights.reduce((a, b) => Math.max(a, b), 0);
  // The last row's trailing gutter isn't part of the container.
  return { placements, byId, height: Math.max(0, tallest - GUTTER), colW, cols };
}

/** Two columns minimum: one column isn't a masonry, it's a list. */
export function columnCount(containerW: number): number {
  return Math.max(2, Math.floor((containerW + GUTTER) / (TARGET_COL_W + GUTTER)));
}

/** Rendered height as a multiple of width, clamped and guarded against junk. */
export function ratio(w: number, h: number): number {
  if (!Number.isFinite(w) || !Number.isFinite(h) || w <= 0 || h <= 0) return 1;
  return Math.min(h / w, MAX_RATIO);
}

function shortest(heights: readonly number[]): number {
  let best = 0;
  for (let i = 1; i < heights.length; i += 1) {
    if ((heights[i] ?? 0) < (heights[best] ?? 0)) best = i;
  }
  return best;
}
